import { useField } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	EvaluationSubmissionGoal,
	EvaluationSubmissionItemReviewGoalInput,
	EvaluationSubmissionParty,
} from '@treams/treams-network';
import { Accordion, AddIcon, Button, Join, Paragraph, Separator } from '@treams/treams-ui';

import SelectGoalsModal from '../SelectGoalsModal';
import { EvaluationSubmissionReviewGoalDetail } from './EvaluationSubmissionReviewGoalDetail';
import EvaluationSubmissionReviewGoalsAccordionFooter from './EvaluationSubmissionReviewGoalsAccordionFooter';

export default function EvaluationSubmissionReviewGoals(props: {
	section: number;
	item: number;
	submission: graphql.EvaluationSubmission;
	fillInBy: { manager: boolean; employee: boolean };
	onSave: () => void;
	isSaving: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const goals = props.submission.goals ?? [];

	const name = `sections[${props.section}].items[${props.item}]`;

	const viewerCanAddGoals =
		(props.submission.viewer.party === EvaluationSubmissionParty.Manager &&
			props.fillInBy.manager) ||
		(props.submission.viewer.party === EvaluationSubmissionParty.Employee &&
			props.fillInBy.employee);

	const [{ value }, , { setValue }] = useField<{
		type: graphql.EvaluationSectionItemType.ReviewGoals;
		reviewGoals?: graphql.EvaluationSubmissionItemReviewGoalInput[];
	}>(name);

	const defaultExplanation = localize(
		props.submission.evaluationForm.sections[props.section]?.items[props.item]?.reviewGoals
			?.defaultExplanation,
	)?.text;

	const [showSelectGoalsModal, setShowSelectGoalsModal] = useState(false);
	const [selectedGoals, setSelectedGoals] = useState<EvaluationSubmissionGoal[]>([]);
	const [lastSelectedGoals, setLastSelectedGoals] = useState<EvaluationSubmissionGoal[]>([]);

	useEffect(() => {
		const managerGoalReviews =
			props.submission.manager?.sections[props.section]?.items[props.item]?.reviewGoals || [];
		const employeeGoalReviews =
			props.submission.employee?.sections[props.section]?.items[props.item]?.reviewGoals || [];
		const currentFormGoalReviews = value.reviewGoals || [];

		const reviewedGoalIDs = new Set(
			[...managerGoalReviews, ...employeeGoalReviews, ...currentFormGoalReviews].map(
				({ goalID }) => goalID,
			),
		);

		const filteredGoals = goals.filter((goal) => reviewedGoalIDs?.has(goal.goal.id));

		setSelectedGoals(filteredGoals);

		setReviewGoalsValue(filteredGoals);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.submission]);

	const save = (goals: EvaluationSubmissionGoal[]) => {
		setReviewGoalsValue(goals, { seedDefault: true });

		setLastSelectedGoals(
			goals.filter((goal) => !selectedGoals.some((selected) => selected.goal.id === goal.goal.id)),
		);
		setSelectedGoals(goals);

		setShowSelectGoalsModal(false);
	};

	const setReviewGoalsValue = (
		goals: EvaluationSubmissionGoal[],
		{ seedDefault = false }: { seedDefault?: boolean } = {},
	) => {
		const reviewGoals = goals.map((goal) => {
			if (
				value.reviewGoals &&
				value.reviewGoals.find((reviewGoal) => reviewGoal.goalID === goal.goal.id)
			) {
				return value.reviewGoals.find((reviewGoal) => reviewGoal.goalID === goal.goal.id)!;
			}

			return {
				goalID: goal.goal.id,
				competency: undefined,
				text: { ops: seedDefault ? [...(defaultExplanation?.ops ?? [])] : [] },
			} as EvaluationSubmissionItemReviewGoalInput;
		});

		setValue({
			...value,
			reviewGoals: [...reviewGoals],
		});
	};

	return (
		<>
			{selectedGoals.length > 0 && (
				<>
					{selectedGoals.map((goal, index) => (
						<Accordion
							noPadding
							initialIsCollapsed={
								!lastSelectedGoals.some((lastGoal) => lastGoal.goal.id === goal.goal.id)
							}
							title={localize(goal.title).text}
							description={
								<Join separator={<>&nbsp;{'·'}&nbsp;</>}>
									{goal.dueDate && (
										<Paragraph>
											{`${t('EvaluationSubmission.reviewGoals.deadline')}: ${moment(
												goal.dueDate,
											).format('L')}`}
										</Paragraph>
									)}
								</Join>
							}
							footer={
								<EvaluationSubmissionReviewGoalsAccordionFooter
									goal={goal}
									name={name}
									selectedGoals={selectedGoals}
									setSelectedGoals={setSelectedGoals}
								/>
							}
							key={index}
						>
							<>
								<Separator />
								<EvaluationSubmissionReviewGoalDetail
									sectionIndex={props.section}
									itemIndex={props.item}
									goalID={goal.goal.id}
									submission={props.submission}
									fillInBy={props.fillInBy}
									onSave={props.onSave}
									isSaving={props.isSaving}
								/>
								<Separator />
							</>
						</Accordion>
					))}
				</>
			)}

			{viewerCanAddGoals && (
				<Box display="inline-block">
					<Button
						onClick={() => setShowSelectGoalsModal(true)}
						icon={<AddIcon />}
						title={t('evaluationSubmission.goalsReflection.select')}
						type="secondary"
					/>
				</Box>
			)}
			{showSelectGoalsModal && (
				<SelectGoalsModal
					goals={goals}
					selectedGoalsProp={selectedGoals}
					onClose={() => setShowSelectGoalsModal(false)}
					onSave={(selectedGoals) => save(selectedGoals)}
				/>
			)}
			<br />
			<br />
		</>
	);
}
